/**
 * LeoSectorChart — subject × UK-region salary strip.
 *
 * Data: `hesa_stats.leo_sec.leo5` (5-year LEO) / `.leo3` (3-year LEO) /
 * `.go` (Graduate Outcomes, 15 months). Each is a `{region_code: {lq,
 * med, uq, pop}}` map from HESA Discover Uni's SectorSal entity.
 *
 * Chart: horizontal bar per region, bar length = median salary (£/yr),
 * highlighted whisker = LQ→UQ span. Regions sorted high→low median.
 * The applicant's own country/region is not known here, so we surface
 * ALL regions and let them eyeball the London-vs-North gap.
 */
import { useState, useMemo } from "react";
import { Ev } from "@/components/app/degree/DegreeDetailBits";

const REGION_LABEL = {
  UK: "UK national",
  E:  "England",
  LN: "London",
  SE: "South East",
  SW: "South West",
  NW: "North West",
  NE: "North East",
  EM: "East Midlands",
  WM: "West Midlands",
  EE: "East of England",
  YH: "Yorkshire & Humber",
  W:  "Wales",
  CF: "Cardiff",
  S:  "Scotland",
  ED: "Edinburgh",
  GL: "Glasgow",
  NI: "Northern Ireland",
};

const TIMEPOINT_LABEL = {
  leo5: "5 years after graduation (LEO)",
  leo3: "3 years after graduation (LEO)",
  go:   "15 months after graduation (Graduate Outcomes)",
};

const CAH_LABEL = {
  CAH01: "Medicine & dentistry",
  CAH02: "Subjects allied to medicine",
  CAH03: "Biological & sport sciences",
  CAH04: "Psychology",
  CAH05: "Veterinary sciences",
  CAH06: "Agriculture, food & related",
  CAH07: "Physical sciences",
  CAH08: "General & others in sciences",
  CAH09: "Mathematical sciences",
  CAH10: "Engineering & technology",
  CAH11: "Computing",
  CAH13: "Architecture, building & planning",
  CAH14: "Geographical & environmental",
  CAH15: "Social sciences",
  CAH16: "Law",
  CAH17: "Business & management",
  CAH18: "Communications & media",
  CAH19: "Languages & area studies",
  CAH20: "Historical, philosophical & religious",
  CAH21: "Design, creative & performing arts",
  CAH22: "Education & teaching",
  CAH23: "Combined & general studies",
  CAH24: "Health & social care",
  CAH25: "Nursing",
  CAH26: "Allied health professions",
};

const currency0 = (n) => n == null ? "—" : `£${Math.round(n).toLocaleString()}`;

function cahReadable(code) {
  if (!code) return null;
  const top = code.split("-")[0]; // "CAH17-01-01" → "CAH17"
  return CAH_LABEL[top] || top;
}

/**
 * @param {{ leoSec?: object }} props — `d.hesa_stats.leo_sec`
 */
export function LeoSectorChart({ leoSec }) {
  const [tp, setTp] = useState("leo5");
  const dataset = leoSec?.[tp] || {};
  const rows = useMemo(() => {
    const arr = Object.entries(dataset)
      .filter(([_, v]) => v && v.med != null)
      .map(([code, v]) => ({ code, label: REGION_LABEL[code] || code, ...v }));
    // Sort high-to-low median. Keep "UK" pinned at the top as the
    // reference line even if higher-earning single regions exist.
    arr.sort((a, b) => (b.med || 0) - (a.med || 0));
    const uk = arr.find((r) => r.code === "UK");
    if (uk) return [uk, ...arr.filter((r) => r.code !== "UK")];
    return arr;
  }, [dataset]);

  if (!leoSec || rows.length === 0) return null;

  const availableTps = ["leo5", "leo3", "go"].filter((k) => {
    const set = leoSec[k] || {};
    return Object.values(set).some((v) => v && v.med != null);
  });

  const maxUq = Math.max(...rows.map((r) => r.uq || r.med || 0), 1);
  const cah = cahReadable(leoSec.cah_matched || leoSec.cah_full);

  return (
    <div className="mt-2" data-testid="leo-sector-chart">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="label-uppercase !text-[10px] text-stone-500">
          Salary by UK region
        </div>
        {availableTps.length > 1 && (
          <div className="flex gap-1 text-[11px]" data-testid="leo-sector-timepoint-switch">
            {availableTps.map((k) => (
              <button
                key={k}
                onClick={() => setTp(k)}
                data-testid={`leo-sector-tab-${k}`}
                className={`px-2.5 py-1 rounded-md border ${
                  tp === k
                    ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                    : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
                }`}
              >
                {k === "leo5" ? "5y" : k === "leo3" ? "3y" : "15m"}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3 space-y-1.5" data-testid="leo-sector-rows">
        {rows.map((r, i) => {
          const medPct = ((r.med || 0) / maxUq) * 100;
          const lqPct  = ((r.lq  || 0) / maxUq) * 100;
          const uqPct  = ((r.uq  || 0) / maxUq) * 100;
          const isUK = r.code === "UK";
          return (
            <div key={r.code}
                 data-testid={`leo-sector-row-${r.code}`}
                 className={`grid grid-cols-[7.5rem_1fr_5rem] items-center gap-2 text-[12px] ${isUK ? "pb-1.5 border-b border-stone-200 mb-1" : ""}`}>
              <div className={`truncate ${isUK ? "font-semibold text-stone-900" : "text-stone-600"}`}
                   title={r.label}>
                {r.label}
              </div>
              <div className="relative h-4 bg-stone-100 rounded-sm overflow-hidden">
                {/* LQ→UQ whisker (thinner) */}
                <div
                  className="absolute inset-y-1.5 rounded-sm bg-emerald-200"
                  style={{ left: `${lqPct}%`, width: `${Math.max(0, uqPct - lqPct)}%` }}
                />
                {/* Median bar to bar-length */}
                <div
                  className={`absolute inset-y-0 rounded-sm ${isUK ? "bg-[#4C1D95]" : "bg-emerald-600"} opacity-60`}
                  style={{ width: `${medPct}%` }}
                />
                {/* Median tick */}
                <div
                  className="absolute inset-y-0 w-0.5 bg-stone-900"
                  style={{ left: `calc(${medPct}% - 1px)` }}
                  title={`Median ${currency0(r.med)}`}
                />
              </div>
              <div className="text-right font-mono tabular-nums text-stone-800">
                {currency0(r.med)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-2 text-[10px] font-mono text-stone-500">
        Dark tick = median · pale bar = LQ→UQ range · {TIMEPOINT_LABEL[tp]}
      </div>
      <Ev ev={{
        source: leoSec.source,
        confidence: "verified",
        url: "https://discoveruni.gov.uk/",
        date_checked: leoSec.generated_at,
      }} />
    </div>
  );
}

export default LeoSectorChart;
